import React from "react";
import GuideLayout from "../../components/layout/GuideLayout";
import Section from "../../components/layout/Section";
import Subheader from "../../components/layout/Subheader";
import {
  Typography,
  Box,
  Link,
} from "@mui/material";
import AccordionFAQ from "../../components/layout/AccordionFAQ";
import BulletList from "../../components/layout/BulletList";
import DefinitionTable from "../../components/layout/DefinitionTable";
import { useTheme } from "@mui/material/styles";

const termList = [
  { term: "NC", definition: "Neocash" },
  { term: "NCC", definition: "Neocash Chat" },
  { term: "TL", definition: "Tradelist" },
  { term: "WL", definition: "Wishlist" },
  { term: "UFT", definition: "Up for trade" },
  { term: "NFT", definition: "Not for trade" },
  { term: "UFA", definition: "Up for auction" },
  { term: "GBC", definition: "Gift Box Capsule" },
  { term: "BFGBC", definition: "Black Friday Gift Box Capsule" },
  { term: "Caps", definition: "Mystery capsules (usually refers to trading value. Generally 1 cap = 200NC, but this can vary.)" },
  { term: "2/3/4:1", definition: "Two/three/four items for 1 GBC" },
  { term: "Cap sale", definition: "A sale where people offer items (often discounted) for gift box caps or other caps." },
  { term: "Ratio", definition: "The ratio of users offering and seeking a certain item." },
  { term: "HTF", definition: "Hard to find" },
  { term: "HTPW", definition: "Hard to part with" },
  { term: "ISO/S/LF", definition: "In search of / Seeking / Looking for" },
  { term: "DISO", definition: "Desperately in search of" },
  { term: "O", definition: "Offering" },
  { term: "JN/DTI", definition: "Jellyneo / Dress to Impress" },
  { term: "DW", definition: "Dyeworks" },
  { term: "Pop", definition: "Popular item" },
  { term: "Custom", definition: "Buying items of the other person's choice from the NC Mall in exchange for NC item(s)." },
  { term: "FQC", definition: "Faerie Quest (Fortune) Cookie" },
  { term: "Upcycle", definition: "NC Upcycle Fortune Cookie" },
  { term: "RR", definition: "Rerelease" },
  { term: "BG", definition: "Background (item zone)" },
  { term: "FG/LFG/HFG", definition: "Foreground / Lower Foreground / Higher Foreground (item zones)" },
  { term: "PC", definition: "Premium Collectible" },
  { term: "LE", definition: "Limited Edition" },
  { term: "OG", definition: "Original (usually refers to an item that has Dyeworks versions)" },
  { term: "Buyable", definition: "Item is available for purchase in the NC Mall." },
  { term: "Retired", definition: "Item is no longer available for purchase in the NC Mall" },
  { term: "Dyepot", definition: "Dyeworks Hue Brew Potion" },
  { term: "MME", definition: "Mysterious Morphing Experiment" },
  { term: "DSSS", definition: "Deluxe Styling Studio Supplies" },
  { term: "SSS", definition: "Styling Studio Supplies" },
  { term: "SSPB", definition: "Styling Studio Prismatic Brush" },
  { term: "SSRP", definition: "Styling Studio Restyle Paint" },
  { term: "Token", definition: "Nostalgic Pet Token (aka UC Style)" },
  { term: "Box", definition: "Gift Box (item used to send NC items to other users)" }
];

const bulletListAllowed = [
  "Purchasing/spending Neocash on sides accounts. (Editorial #810)",
  "Spending Neocash on sides accounts. (Editorial #810)",
  "Under-offering on Neocash trades (unless the person is intentionally misled/cheated) (Editorial #717)",
  "Giving away Neocash card codes (as long as you get no compensation!) (Editorial #600)",
  "Selling NP items gained from redeeming NC cards (Editorial #471)",
  "Offering any amount of NC custom for a NC item (Editorial #437)",
  "Collecting goodie bags on side accounts (as long as they are NC only!) (Editorial #689, #554)",
  "Redeeming NC Cards on side accounts (Editorial #385)",
  "Redeeming free NC on side accounts."
];

const bulletListDisallowed = [
  "Trading NC for NPs/Art/Commissions. (Editorial #487)",
  "Trading NC for pets/items/on site perks. (Editorial #491, #438)",
  "Trading NC items for NC cards.",
  "Playing the Quasalan Expellibox/Trudy's Surprise on your sides (as both give out NPs!)",
  "Using Lab Ray Fortune Cookies on a side account. (Exception: You ONLY have the lab ray unlocked on a side account.)",
  "Trading Nostalgic Pet Tokens for pets."
];

const faqItems = [
  {
    question: "What is Neocash?",
    answer: (<>
      Neocash is a currency on Neopets that is used to purchase items in the NC Mall. Such items include wearables (e.g. clothes, backgrounds, accessories), pet slots, fortune cookies, mystery capsules, and more.
      <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
        <img src={require('../../assets/beginner-guide/ncmallnew.png')} alt="nc mall" style={{ maxWidth: "100%", borderRadius: 8, marginTop: 16 }} />
      </Box>
    </>),
  },
  {
    question: "How do I get Neocash?",
    answer: (<>
      Neocash can be purchased directly from Neopets, from the Neocash Mall page. At the top right there is a "Purchase NC" button. Clicking this will redirect you to the purchase page where you can pay via Credit Card or PayPal.

      <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
        <img src={require('../../assets/beginner-guide/purchase_nc.png')} alt="nc mall purchase" style={{ maxWidth: "100%", borderRadius: 8, marginTop: 16 }} />
      </Box>
      In past, third party sellers did sell NC cards. Unfortunately this method is not widely available anymore due to price increases on the cards.
    </>),
  },
  {
    question: "Can I send NC items to friends?",
    answer: (
      <>
        <Typography paragraph>
          Yes! In fact, a large part of the NC experience is trading. There is a lot to learn about trading, but for now we will just focus on how to send items to another user.
        </Typography>

        <Typography paragraph>
          First, you need a gift box. This item is crucial to trading because you cannot send items without it! When you click on the gift box, if you have NC items in your inventory, you will see a dropdown with each item's name. Select the item you'd like to send, and then hit "Submit."
        </Typography>

        <Typography paragraph>
          <b>IF YOU DO NOT HAVE NEOPASS</b>: The menu will ask you who you want to send it to. You can select a Neofriend from the dropdown, or type in the recipient's account name. <i>It is highly recommended to copy/paste the recipient's username to ensure correct spelling.</i> Once you type in your details, press "Give Item!"
        </Typography>

        <Typography paragraph>
          <b>IF YOU DO HAVE NEOPASS</b>: follow the same steps, except you'll skip typing in your account details-- instead, when you press "Give Item!" another window will open that prompts you to log into your Neopass account. Once you log in, you'll get a Kougra paw making a thumbs up. Don't close this window! It will close automatically after the 5 second countdown.
        </Typography>

        <Typography paragraph>
          You have now sent an NC item!
        </Typography>

        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/sending_nc.gif')} alt="sending nc item gif" style={{ maxWidth: "100%", borderRadius: 8, marginTop: 16 }} />
        </Box>
      </>
    ),
  },
  {
    question: "What if I sent the item to the wrong user?",
    answer: (
      <>
        In the past, NC items would be automatically sent back if not accepted or rejected within 48 hours. <b>THIS IS NO LONGER THE CASE.</b>
        If you mistakenly enter the wrong recipient, you will have to submit a ticket to reverse the trade. This can take anywhere from a few hours to a few days. PLEASE always double, triple check the spelling before sending any items. It is recommended to copy/paste usernames for this reason.
      </>
    ),
  },
];

const sections = [
  { id: "intro", title: "Introduction" },
  {
    id: "basics",
    title: "Basics",
    children: [
      { id: "faq", title: "Basics of Neocash" },
      { id: "legal", title: "What You Can and Can't Do" },
      { id: "acronyms", title: "Acronyms, Abbreviations, and Terms to Know" }
    ]
  },
  {
    id: "trading",
    title: "Trading",
    children: [
      { id: "tradelist", title: "Setting up a Trade List" },
      { id: "values", title: "Understanding Neocash Values" },
      { id: "caps", title: "A Quick Caps Overview" },
      { id: "valuecheck", title: "Finding Values" },
    ]
  },
  {
    id: "styling",
    title: "Styling Studio",
    children: [
      { id: "tokens", title: "Styling Studio Basics" },
      { id: "howtotokens", title: "Using the Styling Studio" }
    ]
  },
  { id: "etiquette", title: "Trade Etiquette" },
  {
    id: "games",
    title: "Games and Activities",
    children: [
      { id: "shenanigifts", title: "Shenanigifts" },
      { id: "corridor", title: "Castle Nox Corridor of Chance" },
      { id: "dyeworks", title: "Dyeworks" },
      { id: "mme", title: "Mysterious Morphing Experiments" },
      { id: "kaia", title: "Kaia's Quests" },
    ]
  },
  {
    id: "events",
    title: "Annual Events",
    children: [
      { id: "weekend-sales", title: "Weekend Sales" },
      { id: "neopies", title: "Neopies" },
      { id: "valentines-day", title: "Valentine's Day" },
      { id: "spring-cleaning", title: "Spring Cleaning" },
      { id: "negg-festival", title: "Negg Festival" },
      { id: "nc-mall-overstock", title: "NC Mall Overstock" },
      { id: "altador-cup", title: "Altador Cup" },
      { id: "nc-mall-birthday", title: "NC Mall Birthday" },
      { id: "faerie-festival", title: "Faerie Festival" },
      { id: "halloween", title: "Halloween" },
      { id: "black-friday", title: "Black Friday" },
      { id: "stocking-stufftacular", title: "Stocking Stufftacular" },
    ]
  },
];

const BeginnerGuide = () => {
  const theme = useTheme();

  return (
    <GuideLayout title="intro" sections={sections}>
      <Section id="intro" title="Beginner's Guide to Neocash">
        <Typography
          variant="caption"
          color="secondary"
          sx={{ mt: -1, mb: 2, display: "block" }}
        >
          Last updated: June 2026. Also hosted on <Link sx={{ color: theme.palette.secondary.main }} href="https://neopets.com/~stylisher">neopets.com/~stylisher</Link>.
        </Typography>
        <Typography paragraph>
          Welcome to <i>Stylisher</i> — Your Beginner's Guide to Neocash!
        </Typography>
        <Typography paragraph>
          Whether you’re brand new to NC trading or just need a refresher, you’ve come to the right place. Stylisher is designed to help you get comfortable with the world of Neocash (NC) — from understanding how trades work to figuring out item values and navigating the community.
        </Typography>
        Here, you’ll find everything you need to start off strong, including:
        <BulletList items={[
          "Common acronyms you'll see in NC discussions",
          "Trading etiquette to help you communicate clearly and respectfully",
          "Tips on understanding values",
          "Info on seasonal and annual events",
          "And much more!"
        ]} />
        <Typography paragraph>
          NC trading can feel overwhelming at first, but with the right tools and knowledge, it can quickly become a fun and rewarding part of your Neopets experience. Let’s get started!
        </Typography>
      </Section>

      <Section id="faq">
        <AccordionFAQ subheader={
          <Typography
            variant="h4"
            gutterBottom
            sx={theme => ({
              color: theme.palette.accent.main
            })}>
            The Basics of Neocash
          </Typography>}
          items={faqItems} />
      </Section>

      <Section id="legal" title="What You Can and Can't Do">
        <Subheader title="Allowed" />
        <BulletList items={bulletListAllowed} />
        <Subheader title="Not Allowed" />
        <BulletList items={bulletListDisallowed} />
      </Section>

      <Section id="acronyms" title="Acronyms, Abbreviations, and Terms to Know">
        <DefinitionTable termList={termList} />
      </Section>

      <Section id="tradelist" title="Setting up a Trade List">
        <Typography paragraph>
          To start trading, set up a tradelist. This lets others see which items you have available to trade and what you're looking for. We'll discuss different sites for tradelists below.
        </Typography>
        <Typography paragraph>
          Once your tradelist is ready, you can use the NC Trading boards or sites like JN/DTI to find trades and contact other users.
        </Typography>

        <Subheader title="Dress To Impress" />
        <Typography paragraph>
          <Link href="https://impress.openneo.net/">Dress To Impress</Link> (DTI) is a fan-made site for setting up tradelists, wishlists, trying on wearables, and more. It features an import tool for quickly adding items from your closet or safety deposit box. DTI is widely used in the NC trading community.
        </Typography>
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/dti.png')} alt="DTI" style={{ maxWidth: "80%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          There are two versions of DTI: the classic site and DTI2020. Both work for sharing tradelists. 2020 was originally created to modernize the site, but the creator later decided to focus on updating the classic version instead. As a result, DTI2020 is no longer being updated, and the classic version is now the main site.
        </Typography>

        <Subheader title="Jellyneo" />
        <Typography paragraph>
          <Link href="https://items.jellyneo.net/">Jellyneo</Link> (JN) is another fan-made site with features like wishlists and item databases. While not as user-friendly as DTI, it's still popular. JN allows adding non-wearables to wishlists, so some users use both sites: DTI for wearables and JN for non-wearables.
        </Typography>
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/jn.png')} alt="JN" style={{ maxWidth: "80%", borderRadius: 8, marginTop: 16 }} />
        </Box>
      </Section>

      <Section id="values" title="Understanding Neocash Values">
        <Typography paragraph>
          Neocash values are influenced by multiple factors, including rarity, demand, and market trends. It's important to note that the community sets most values, not TNT. Here are some key factors:
        </Typography>

        <Subheader title="Popularity" />
        <Typography paragraph>
          Popular items are sought after and may have higher value. These are often called "pops."
        </Typography>

        <Subheader title="Rarity" />
        <Typography paragraph>
          Rare items, such as those retired years ago, tend to be more valuable. Buyable items are usually lower in value due to their abundance.
        </Typography>

        <Subheader title="Cost to Obtain" />
        <Typography paragraph>
          Items available in the mall, like DSSS, Mystery Caps, and Superpacks, are typically valued at cost. For example, DSSS costs 1400NC and is valued at 7 caps. An exception to this is Mystery Cap LEs, which are usually valued higher despite being buyable, due to their lower supply and higher demand.
        </Typography>

        <Subheader title="Personal Taste" />
        <Typography paragraph>
          Personal preferences affect value. You may value an item more than others if you like it, regardless of its community value.
        </Typography>

        <Subheader title="Conclusion" />
        <Typography paragraph>
          In general, items with high supply and low demand are valued lower, while those with high demand and low supply are valued higher. Value trackers like Lebron and Valisar update values based on user reports, but always consider your own preferences.
        </Typography>
      </Section>

      <Section id="caps" title="A Quick Caps Overview">
        <Typography paragraph>
          Caps are the standard NC trading currency, short for Gift Box Mystery Capsules (GBCs). GBCs cost 200NC and always reward at least two gift boxes when opened. Since you need gift boxes to send items, they're the backbone of trading, which is why items are valued in caps. There is always a buyable GBC in the mall for 200NC. There is a different design each month, but look for the item that contains "Gift Box Mystery Capsule" in the name and check the item description before buying!
        </Typography>
        <Typography paragraph>
          <b>1 cap = 200NC.</b> Some mall capsules cost 150-250NC, but they're still generally worth 1 cap since they require 1 box to send. Most buyable capsules are valued at 1 unless retired.
        </Typography>
        <Typography paragraph>
          Values are usually shown as ranges (e.g., 1-2 or 3-5). Check Lebron for current values on wearables and non-wearables alike!
        </Typography>
        <Typography paragraph>
          Trades usually involve items of similar value, or multiple items adding up to the agreed upon value. Some users offer "custom," meaning they buy a set amount of NC worth of items from the mall for you in exchange for your item. For example, a 350NC custom means you choose 350NC worth of items for them to purchase and send to you.
        </Typography>
      </Section>

      <Section id="valuecheck" title="Finding Values">
        <Typography paragraph>
          So what do you do if your item is not listed with a value? There are tons of NC items out there, so don’t stress—it’s normal to be unsure about value sometimes. Even the most experienced traders often ask for value checks.
        </Typography>
        <Typography paragraph>
          One easy way to get a sense of value is to ask around for a value check. Getting a second opinion can really help during a trade.
        </Typography>
        <Typography paragraph>
          Still not sure? Check the offer/seek ratio. If tons of people are offering it but not many are seeking, it's probably low value (you’ll see these a lot in 2:1 trades). On the flip side, fewer offerers and more seekers usually means it’s more sought after.
        </Typography>
        <Typography paragraph>
          For older items, try looking it up on Waka—it's outdated, but still useful for some older stuff that hasn't been traded in a while.
        </Typography>
        <Typography paragraph>
          And if you're still stuck? Just talk it out with the other trader—sometimes it comes down to what feels fair for both sides.
        </Typography>
        <Typography paragraph>
          For a really deep dive into values, check out <Link href="/articles/values">our guide</Link>!
        </Typography>
      </Section>

      <Section id="tokens" title="Styling Studio Basics">
        <Subheader title="What is the Styling Studio?" />
        <Typography paragraph>
          The Styling Studio offers alternative art styles for pets called "Pet Styles." The Styling Studio allows you to change your pet's appearance using these styles, which can be purchased with Neocash. Each style has its own unique look and can be applied to your pet through the Styling Chamber.
        </Typography>

        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/styling_studio.png')} alt="Styling Studio" style={{ maxWidth: "90%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          There are a variety of styles offered, including Nostalgic Styles, which are based on classic Neopets art. The studio also offers modern designs and colors, such as Valentine and Plushie Baby.
          Styles rotate periodically, so check the news for updates.
        </Typography>
      </Section>

      <Section id="howtotokens" title="Using the Styling Studio">
        <Subheader title="SSS & DSSS" />
        <Typography paragraph>
          To use the studio, you’ll need the correct pet species and a Studio Supplies NC item. These can be purchased in the NC Mall or through a pop-up on the Styling Studio page.
        </Typography>
        <Typography paragraph>
          Styling Studio Supplies (600NC) gives you a random style for the species you choose. Deluxe Styling Studio Supplies (1400NC) lets you choose the style you want. In both cases, the style is applied immediately to the pet.
        </Typography>
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={'https://images.neopets.com/items/mall_ss_supplies.gif'} alt="Styling Studio Supplies" style={{ maxWidth: "100%", borderRadius: 0, marginTop: 8 }} />
          <img src={'https://images.neopets.com/items/mall_ss_deluxesupplies.gif'} alt="Deluxe Styling Studio Supplies" style={{ maxWidth: "100%", borderRadius: 0, marginTop: 8 }} />
        </Box>

        <Typography paragraph>
          To get started, visit the Styling Studio with your pet and supplies ready. Select a species to view available styles. You can only style pets you currently own—so if you want a style for a species you don’t have, you’ll need to borrow one or trade for the style token.
        </Typography>
        <Typography paragraph>
          <strong>Note:</strong> Pets with a style can only wear certain wearable types (like backgrounds or foregrounds). Incompatible items will be removed and returned to your closet.
        </Typography>

        <Subheader title="Prismatics" />
        <Typography paragraph>
          In addition to SSS and DSSS, the Styling Studio Prismatic Brush (250NC) is an NC item that lets you recolor an existing NC Pet Style into a random Prismatic version.
        </Typography>

        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={'https://images.neopets.com/items/h2bb8ihj4c.gif'} alt="Styling Studio Prismatic Brush" style={{ maxWidth: "100%", borderRadius: 0, marginTop: 8 }} />
        </Box>
        <Typography paragraph>
          Similarly to regular pet styles, you can view and purchase Prismatic styles directly from the Styling Studio page in the NC Mall. Your pet must match the <strong>species</strong> of the style, but not the color. Once used, the original style stays in your inventory, and the new color variant is added alongside it.
        </Typography>

        <Subheader title="Restyling" />
        <Typography paragraph>
          Styling Studio Restyle Paints (300NC) are also used within the Styling Studio. When using a Restyle paint, you’ll trade 3 of your tokens for a <strong>random reward</strong> of 1–3 items. These can include a DSSS, SSS, SSPB, and a <strong>random species + color token</strong>.
        </Typography>
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={'https://images.neopets.com/items/0a8877fcf4.gif'} alt="Restyle Studio" style={{ maxWidth: "100%", borderRadius: 0, marginTop: 8 }} />
        </Box>

        <Typography paragraph>
          <strong>Note:</strong> You may receive tokens for species you don’t own, so choose your restyle tokens carefully. Brushes will not give permanent mall tokens or unreleased ones.
        </Typography>
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/style_meter.png')} alt="Restyling" style={{ maxWidth: "90%", borderRadius: 8, marginTop: 16 }} />
        </Box>

        <Typography paragraph>
          Once you've used 15 restyling paints, you can choose from 10 exclusive NPC Styling Items (often referred to as Essence Tokens). Just like regular tokens, these items are species specific. Essences are not available in the NC Mall and can only be obtained through restyling. They will also cycle out periodically, so be sure to check the news for updates.
        </Typography>

        <Subheader title="Removing a Pet Style" />
        <Typography paragraph>
          To remove a pet style, navigate over to the Styling Chamber. This is only possible from the new mobile-friendly layout. Click on your active pet's icon at the top left corner. Then click the arrow next to "My Pets," and then "Styling Chamber".
        </Typography>
        <Typography paragraph>
          From there you can select your pet that you want to add/remove a token to, click on the token, and select either "Remove Style" or "Apply to Pet". If you don't own a token for a specific species, no tokens will appear. Toggle "Show All" to have all of your owned tokens appear.
        </Typography>
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/styling_chamber.png')} alt="Styling Chamber" style={{ maxWidth: "100%", borderRadius: 8, marginTop: 16 }} />
        </Box>
      </Section>

      <Section id="etiquette" title="Trade Etiquette">
        <Subheader title="et·i·quette" />
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          /ˈedəkət,ˈedəˌket/
        </Typography>
        <Typography paragraph>
          noun — the customary code of polite behavior in society or among members of a particular group.
        </Typography>

        <Subheader title="DO" />
        <BulletList items={[
          "Be kind, polite, and understanding.",
          "Keep messages short and to the point — no one wants to read an essay.",
          "Keep NC and NP lists separate.",
          "Clearly state your trade preferences (e.g., if you’re only trading certain items for wishes).",
          "Respect value disagreements — it’s okay to value things differently!",
          "Be patient. Not everyone is online all day; 24 hours is a common grace period for replies.",
          "Send confirmation after you’ve sent your part of the trade.",
          "Always follow through — send the exact items you promised.",
          "Read tradelists fully — they often include contact info or trade rules."
        ]} />

        <Subheader title="DON’T" />
        <BulletList items={[
          "Set your closet (or NFT items) to “Trading.” If you want to show off, use a gallery or petpage.",
          "Harass or pressure other players for any reason.",
          "Accept any part of a trade until both sides have sent their items.",
          "Publicly call out users, on or offsite. If needed, report or submit a support ticket — calling out users can get you in trouble.",
          "Assume where to send items — always wait for confirmation.",
          "Use excessive acronyms or shorthand in listings — be clear so others know what you mean.",
          "Ghost during trades. If you need to back out, just let the other person know."
        ]} />

        <Subheader title="What if I sent my part of a trade but haven’t received anything?" />
        <Typography paragraph>
          Be patient. If it’s been over 24 hours, message the user to check in — it could be a mistake or a delay. If you think you’ve been scammed, submit a support ticket right away. TNT is usually good about reversing scam trades, though it may take time.
        </Typography>

        <Subheader title="What if I think someone’s offer is unfair?" />
        <Typography paragraph>
          Simple: don’t trade with them. It’s always okay to say no — you’re never obligated to accept any trade.
        </Typography>
      </Section>

      <Section id="games" title="Games and Activities">
        <Typography paragraph>
          In addition to buying and trading NC items, there are also various games and activities that you can participate in to earn exclusive NC prizes. Here are some games and activities:
        </Typography>
        <Subheader id="shenanigifts" title="Shenanigifts" />
        <Typography paragraph>
          Shenanigifts is a game that allows you to win special prizes from the party of your choice. First you must purchase an invitation. You can buy just one, or you can buy in bundles. You can also purchase power-ups that will give you extra chances, double gifts, or a second look at a prize you already saw. Once you're in the party, you can click on the guests and they'll show you a prize. You can either keep it, or move on to another guest with a different prize.
        </Typography>
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/shenanigifts.png')} alt="Shenanigifts" style={{ maxWidth: "100%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Subheader id="corridor" title="Castle Nox Corridor of Chance" />
        <Typography paragraph>
          Castle Nox is a themed NC Mall game in Neopets’ Haunted Woods as part of the Faerie's Ruin plot. It features a Corridor of Chance accessible via purchasable keys.
        </Typography>
        <Typography paragraph>
          You must buy Key Packs (1‑pack, 10‑pack, or 20‑pack) from the Specialty shop in the NC Mall. Once activated from your inventory, each key grants access to the Corridor and a single pull of the Transfabulator. The Transfabulator transforms an NC‑style wearable item your pet is currently wearing into a new exclusive NC wearable. Your old item goes to your closet, and you receive the new item. The new color or item is randomized within the pool.
        </Typography>
        <Subheader id="dyeworks" title="Dyeworks" />
        <Typography paragraph>
          Dyeworks is a seasonal event that takes place in the NC Mall. While limited time Dyeworks are available, select items that match the season are made available in new colors. To dye an item, you need both the original item and a dyepot. The dyepot is consumed in the process, but you get to keep the original item.
        </Typography>
        <Typography paragraph>
          When you visit the Dyeworks page, the items are organized into categories. You must click on the correct category to find the item you want to dye. Once you locate the item, click on its icon and confirm that you want to dye it. You’ll receive one random color version from the available options.
        </Typography>
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/dyeworks.gif')} alt="Dyeworks" style={{ maxWidth: "100%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Subheader id="mme" title="Mysterious Morphing Experiments" />
        <Typography paragraph>
          Mysterious Morphing Experiments (MMEs) are special items sold on Neopets’ NC Mall. Each MME is available in the mall for a couple of weeks. After sale ends, the item will begin morphing across multiple stages, turning into different wearable items. Once the transformation is complete, you will receive all previous forms in your inventory. You also have a chance to receive a bonus item, which is usually a wearable that matches the overall MME theme.
        </Typography>
        <Typography paragraph>
          MMEs may have different IDs, corresponding to branching paths (e.g. Stage a or b). To obtain a specific branch, you need to be mindful of the MME ID in your Shopping Cart. In the new NC Mall layout (as of June 2025), identical MMEs are stacked in the cart by ID; having two of the same item that are NOT stacked (the quantity will say 1) ensures you have one of each ID. In the old layout, inspecting HTML tags or “Inspect Element” can help you find the ID of the MME you want to purchase.
        </Typography>

        <Subheader id="kaia" title="Kaia's Quests" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/kaia.png')} alt="Kaia's Quests" style={{ maxWidth: "100%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          Kaia, the NC Faerie, appears during Faerie Quest Random Events. She will request an NC item from you, in exchange for an exclusive NC item that she gives out. In order to get a request from her, you must have an NC item in your inventory.
        </Typography>
        <Typography paragraph>
          To turn in an item for Kaia's quest you must have a Pink Paper Bag which is buyable from the mall for 75 NC. Once you have the NC item and the Paper Bag, use the dropdown menu to select the NC item and click “Give her the items” to turn them in and claim an exclusive prize!
        </Typography>
        <Typography paragraph>
          If her request mentions a specific word (e.g. “blue” or “sparkly”) you must have an NC item whose name includes that word. Alternatively, remove those matching items from your inventory, and she’ll accept any NC item instead.
        </Typography>
        <Typography paragraph>
          If you don't want to complete the quest, you can abandon it.
        </Typography>
      </Section>

      <Section id="events" title="Annual Events">
        <Typography paragraph>
          Neopets has a variety of annual events that feature new NC items and themed activities. In this section we'll go over what they are and what to expect. Jellyneo also has a great <Link href="https://www.jellyneo.net/?go=neocash">Neocash guide</Link> that lists all past events, and goes into great detail about how they work, what items were given out, and more.
        </Typography>

        <Subheader id="weekend-sales" title="Weekend Sales" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/weekendsales2020.png')} alt="Weekend Sales" style={{ maxWidth: "70%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          During the month of January, Neopets holds weekend sales where you can get discounts on NC items. These sales typically happen over three weekends, with each weekend offering different items at discounted prices. It's a great opportunity to stock up on NC items you may have missed or to try out new wearables. There are also special NC items released during these sales, which can include exclusive wearables, backgrounds, and more.
        </Typography>

        <Subheader id="neopies" title="Neopies" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/neopies_logo.png')} alt="Neopies" style={{ maxWidth: "100%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          The Neopies are a special event that takes place during the beginning of the year, where players can vote for their favorite new releases from the past year. There is also typically a special NC portion of the event, which allows players to buy tickets to receive exclusive NC items. These items can include wearables, backgrounds, and other themed items that celebrate the Neopies event.
        </Typography>

        <Subheader id="valentines-day" title="Valentine's Day" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/valentine_releases.jpg')} alt="Valentine's Day" style={{ maxWidth: "90%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          Valentine's Day is a time for love and friendship, and Neopets celebrates with special NC items called "Sweetheart Grams." These items can be sent to friends without having to use a box, and include themed wearables, backgrounds, and trinkets. There are typically three different Grams that are available to purchase from the NC Mall, each with its own unique items.
        </Typography>
        <Typography paragraph>
          Grams can be sent to yourself, so don't worry if you don't have any Neofriends to trade them with!
        </Typography>

        <Subheader id="spring-cleaning" title="Spring Cleaning" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/spring_cleaning_gbmc.png')} alt="Spring Cleaning" style={{ maxWidth: "70%", borderRadius: 8, marginTop: 16 }} />
        </Box>


        <Typography paragraph>
          The Spring Cleaning sale is a time for the NC Mall to clean out some of those dusty old GBCs that were stuck at the back of the shelves. During a weekend in April, five (5) previously released GBCs return to the mall at a discounted price!
        </Typography>
        <Typography paragraph>
          On the first day, they are priced at 75 NC (around 60% off). On the following day, they will be priced at 100 NC (50% off). These GBCs are NOT BFGBCS. They only award two boxes at minimum; they are simply discounted regular GBCs.
        </Typography>

        <Subheader id="negg-festival" title="Negg Festival" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/dip_dye.png')} alt="Negg Festival" style={{ maxWidth: "80%", borderRadius: 8, marginTop: 16 }} />
        </Box>

        <Typography paragraph>
          The Negg Festival is a celebratory time on Neopets, and players can look forward to the Negg Festival! Much like other events, the Negg Festival offers an NC portion where players can purchase exclusive items. This event is a great way to celebrate the season and add some fun new items to your collection.
        </Typography>
        <Typography paragraph>
          Here's how it works: Activating a negg kit (such as "Cherry Bomb Negg Dying Kit") will award you all of the items, including the bonus item for that set.
        </Typography>
        <Typography paragraph>
          Activating all three of the kits will award you all of the items for each set, including the bonus items for each set. You will also receive the final bonus prizes at the end of the event.
          Single negg kits are available for purchase as well. These will allow you to obtain an item from any day that has already had an item awarded. For example, if you only wanted one of an item, or a dupe of an item, you could use the single kit.
        </Typography>
        <Typography paragraph>
          The kits can only be activated once the prizes from the negg before it have all been awarded. The first kit will be active on the first day of the event. You do not need to claim the items everyday, but you can if you want to. At the end of the event you can activate any/all kits and receive all the prizes at once.
        </Typography>

        <Subheader id="nc-mall-overstock" title="NC Mall Overstock" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/overstock.jpg')} alt="NC Mall Overstock" style={{ maxWidth: "80%", borderRadius: 8, marginTop: 16 }} />
        </Box>

        <Typography paragraph>
          The NC Mall Overstock is a special event that occurs during May, when the NC Mall brings back certain retired items. During this event, players can purchase these overstocked items at their original buyable price. It's a great opportunity to snag some unique wearables and backgrounds that may have been previously unavailable or hard to find.
        </Typography>

        <Subheader id="altador-cup" title="Altador Cup" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/altador.png')} alt="Altador Cup" style={{ maxWidth: "80%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          The Altador Cup is a major event in Neopia, and it often includes a special NC portion. During this event, players can purchase exclusive NC items that celebrate the Altador Cup. These items can include wearables, backgrounds, and other themed items that reflect the spirit of the Altador Cup.
        </Typography>
        <Typography paragraph>
          "Altador from the Clouds" is the NC portion of the Altador Cup, though not held every year. During this event, players can purchase Boarding Passes to receive exclusive NC items. These items can include wearables, backgrounds, and other themed items that celebrate the Altador Cup.
        </Typography>

        <Subheader id="nc-mall-birthday" title="NC Mall Birthday" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/birthday.jpg')} alt="NC Mall Birthday" style={{ maxWidth: "90%", borderRadius: 8, marginTop: 16 }} />
        </Box>

        <Typography paragraph>
          The NC Mall Birthday is a celebration of the NC Mall's launch, and it typically features special NC items, such as mystery capsules, and special grams.
        </Typography>

        <Subheader id="faerie-festival" title="Faerie Festival" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/free_faeries.png')} alt="Faerie Festival" style={{ maxWidth: "90%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          The Faerie Festival is a magical event that celebrates the faeries of Neopia. Players can expect special NC items, quests, and activities that highlight the enchanting world of faeries.
        </Typography>
        <Typography paragraph>
          "Free the Faeries" is the NC portion of the Faerie Festival, where players can purchase slingshots and free the faeries from the jars they have been trapped in! Freeing a faerie grants you a specific NC item. This event is a great way to immerse yourself in the faerie lore of Neopia and collect unique items.
        </Typography>

        <Subheader id="halloween" title="Halloween" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/hauntedmansion.png')} alt="halloween" style={{ maxWidth: "90%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          Halloween is a spooky time on Neopets, and players can expect a variety of themed NC items and events. The NC event "Haunted Mansion" allows players to "explore" a haunted mansion filled with some exclusive, spooky NC items. During this event, players can purchase tickets to access areas in the mansion and its curious treasures.
        </Typography>

        <Subheader id="black-friday" title="Black Friday" />
        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/black_friday.jpg')} alt="black friday banner" style={{ maxWidth: "90%", borderRadius: 8, marginTop: 16 }} />
        </Box>
        <Typography paragraph>
          Black Friday is a major shopping event that takes place in late November. During this time, the NC Mall offers special discounts and sales on various items.
        </Typography>
        <Typography paragraph>
          Rerelease capsules are 50% off, at 75 NC each. Certain Fortune cookies also go on sale including Faerie Quest Fortune Cookie for 50 NC, Lab Ray Fortune Cookie for 75 NC, and NC Archives Fortune Cookie for 150 NC.
        </Typography>
        <Typography paragraph>
          Last but certainly not least, a special type of Gift Box Capsule is available for 100 NC. Keep in mind that the monthly GBC and the BFGBC are identical at first glance! The only difference is the item ID and description. Be sure to double check which one you have before using it!
        </Typography>

        <Subheader id="stocking-stufftacular" title="Stocking Stufftacular" />

        <Box sx={{ textAlign: "center", m: 2, mb: 3 }}>
          <img src={require('../../assets/beginner-guide/stocking_stufftacular.jpg')} alt="christmas" style={{ maxWidth: "90%", borderRadius: 8, marginTop: 16 }} />
        </Box>

        <Typography paragraph>
          Stocking Stufftacular is a holiday event that celebrates the Christmas season. Players can expect a variety of Holiday themed NC items, including backgrounds, clothing, trinkets, and more! There are also special bonus prizes for participating in the event.
        </Typography>
        <Typography paragraph>
          During this event, players can purchase 25 stockings to hang up each day leading up to the New Year, and receive a special NC item each day. Stockings must be hung up the by the night before to receive the item. For example, if you want the prize for December 25th, you must hang up the corresponding stocking by December 24th at 11:59 PM NST.
        </Typography>
      </Section>
    </GuideLayout>
  );
};

export default BeginnerGuide;
